import { Component, OnInit, Output, EventEmitter, Input, signal, ViewChild, computed } from '@angular/core';

import { ReactiveFormsModule, FormGroup, FormBuilder, FormArray, Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { arrayIsSet, GlobalVariablesService, stringIsSet } from 'src/app/providers/service/global-variables.service';
import { ElementParse_v2 } from 'src/app/ParseClass/Element.Parse';
import { map, startWith } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDivider } from "@angular/material/divider";
import { dataForm, GroupDataForm } from '../auto-complete-form-standalone/auto-complete-form-standalone.component';
import { AutoCompleteFormStandaloneComponent } from '../auto-complete-form-standalone/auto-complete-form-standalone.component';
import { PipesModule } from 'src/app/providers/pipes/pipes.module';
import { MatBadge } from "@angular/material/badge";
import { toSignal } from '@angular/core/rxjs-interop';

type ExternalCommand = {
  dataType: string;
  min: number;
  max: number;
  elementId: string;
};

class ExternalCommands {
  [commandId: string]: ExternalCommand;
}

@Component({
    selector: 'app-form-field-external-command',
    imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatMenuModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    TranslateModule,
    MatDivider,
    AutoCompleteFormStandaloneComponent,
    PipesModule,
    MatBadge
],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FormFieldExternalCommandComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FormFieldExternalCommandComponent
        }
    ],
    templateUrl: './form-field-external-command.component.html',
    styleUrl: './form-field-external-command.component.scss'
})
export class FormFieldExternalCommandComponent implements OnInit {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @Output() commandsChange = new EventEmitter<ExternalCommands>();
  @Input() title: string = 'External Commands';
  elementsSignal = signal<ElementParse_v2[] | undefined>(undefined);

  @Input() set elements(value: ElementParse_v2[] | undefined) {
    this.elementsSignal.set(value);
  }

  periodDataValuesElement = computed(() => {
    const elements = this.elementsSignal();
    const periodicDatas = new Set<string>();
    if (arrayIsSet(elements)) {

      elements.forEach(element => {
        if (element.type && element.type.possibleCommands) {
          Object.keys(element.type.possibleCommands).forEach(key => periodicDatas.add(key));
        }
      }
      );
      const keysPeriodicData = Array.from(periodicDatas);
      let groups: GroupDataForm[] = []
      if (arrayIsSet(keysPeriodicData)) {
        groups.push({
          groupName: this.ts.instant('suggested'),
          possibleValues: keysPeriodicData.map(key => {
            const obj: dataForm = {
              valueForm: key,
              html: key,
              traduction: 'possibleCommands.' + key,
            }
            return obj
          })
        });
      }
      return groups;
    }
    return [];
  });
  get elements() {
    return this.elementsSignal()
  }
  commandForm: FormGroup;
  numbersCommand
  externalCommands: ExternalCommands = {};
  subscriptions = new Subscription()

  dataTypes = [
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
  ];

  constructor(private fb: FormBuilder,
    private globalVariablesService: GlobalVariablesService,
    private ts: TranslateService
  ) {
    this.commandForm = this.fb.group({
      commands: this.fb.array([]),
    });
    this.numbersCommand = toSignal(
      this.commandForm.get('commands')!.valueChanges.pipe(
        startWith(undefined),
        map((commands: any[]) => arrayIsSet(commands) ? commands.length : undefined)
      )
    );
  }

  ngOnInit(): void {
    this.commandForm.valueChanges.subscribe(() => {
      this.updateExternalCommands();
    });
  }

  onTouched: Function = () => {
  };

  registerOnChange(onChange: any) {
    const values$ = this.commandForm!.get('commands')!.valueChanges.pipe(
      map(value => {
        // Trasforma l'array in oggetto ExternalCommands
        const externalCommands: ExternalCommands = {};
        if (arrayIsSet(value)) {
          value.forEach((command: any) => {
            if (command.commandId && command.elementId && command.dataType) {
              externalCommands[command.commandId] = {
                dataType: command.dataType,
                min: command.min,
                max: command.max,
                elementId: command.elementId
              };
            }
          });
        }
        return externalCommands;
      })
    )
    this.subscriptions.add(
      values$
        .subscribe(onChange)
    );

  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.commandForm.disable();
    } else {
      this.commandForm.enable();
    }
  }

  writeValue(value: ExternalCommands) {
    if (value) {
      setTimeout(() => {
        // Pulisce il FormArray esistente
        while (this.commandsFormArray.length !== 0) {
          this.commandsFormArray.removeAt(0);
        }

        // Converte l'oggetto ExternalCommands in array di FormGroup
        Object.keys(value).forEach(commandId => {
          const command = value[commandId];
          const commandGroup = this.fb.group({
            commandId: [commandId, [Validators.required]],
            dataType: [command.dataType, [Validators.required]],
            min: [command.min, [Validators.required, Validators.min(0)]],
            max: [command.max, [Validators.required, Validators.min(0)]],
            elementId: [command.elementId, [Validators.required]]
          });
          this.commandsFormArray.push(commandGroup);
        });
      })
    } else {
      this.commandForm.reset();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // if (!isNotNullOrUndefined(this.commandForm.get('autoComplete')?.validator)) {
    //     this.commandForm.get('autoComplete')?.setValidators(control.validator)
    //     this.commandForm.get('autoComplete')?.updateValueAndValidity()
    // }
    if (this.commandForm.valid) {
      return null
    } else {
      return this.commandForm.errors!;
    }
  }

  addControlErrors(allErrors: any, controlName: string) {
    const errors = { ...allErrors };
    const controlErrors = this.commandForm.controls[controlName].errors;
    if (controlErrors) {
      errors[controlName] = controlErrors;
    }
    return errors;
  }

  registerOnTouched(onTouched: Function) {
    this.onTouched = onTouched;
  }
  get commandsFormArray(): FormArray {
    return this.commandForm.get('commands') as FormArray;
  }

  addCommand(): void {
    const commandGroup = this.fb.group({
      commandId: ['', [Validators.required]],
      dataType: ['', [Validators.required]],
      min: [undefined,],
      max: [undefined,],
      elementId: ['', [Validators.required]]
    });

    this.commandsFormArray.push(commandGroup);
  }

  removeCommand(index: number): void {
    this.commandsFormArray.removeAt(index);
  }

  private updateExternalCommands(): void {
    const commands: ExternalCommands = {};

    this.commandsFormArray.controls.forEach(control => {
      const value = control.value;
      if (value.commandId && value.dataType && value.elementId) {
        commands[value.commandId] = {
          dataType: value.dataType,
          min: value.min,
          max: value.max,
          elementId: value.elementId
        };
      }
    });

    this.externalCommands = commands;
    this.commandsChange.emit(this.externalCommands);
  }

  getCommandFormGroup(index: number): FormGroup {
    return this.commandsFormArray.at(index) as FormGroup;
  }

  getElementNamePredicate(elementId: string | undefined, elements: ElementParse_v2[]): string | undefined {
    let elementName = elementId
    if (arrayIsSet(elements)) {
      const element = elements.find(el => el.objectId === elementId)
      if (element != null && stringIsSet(element.name)) {
        elementName = element.name
      }
    }
    return elementName;
  }

  possibleValuesElementsPredicate(elements: ElementParse_v2[] | undefined, dataType: string, suggestedTrad: string, otherTrad: string) {
    if (arrayIsSet(elements)) {
      let suggested
      let others
      if (!stringIsSet(dataType)) {
        suggested = elements
        others = [] as any[]
      } else {
        suggested = elements.filter(e => e.type && e.type.periodicData && Object.keys(e.type.periodicData).includes(dataType));
        others = elements.filter(e => e.type && (!e.type.periodicData || !Object.keys(e.type.periodicData).includes(dataType)));
      }


      let groups: GroupDataForm[] = []
      if (arrayIsSet(suggested)) {
        groups.push({
          groupName: suggestedTrad,
          possibleValues: suggested.map(el => {
            const obj: dataForm = {
              valueForm: el.objectId!,
              html: el.name,
              registerValue: el.objectId!
            }
            return obj
          })
        });
      }
      if (arrayIsSet(others)) {
        groups.push({
          groupName: otherTrad,
          possibleValues: others.map(el => {
            const obj: dataForm = {
              valueForm: el.objectId!,
              html: el.name,
              registerValue: el.objectId!
            }
            return obj
          })
        });
      }
      return groups;
    }
    return [];
  }
}
